import { ElectronApi } from '@/services/electron.api';
import mime from 'mime';

/**
 * 返回是否为图片类型文件
 * @param file
 * @param ext [可选] 不含点的图片后缀
 * @returns
 */
export const isImageFile = (file: string, ext?: string) => {
  return (
    !!mime.getType(file)?.includes('image') &&
    (ext ? file.toLowerCase().endsWith(ext.toLowerCase()) : true)
  );
};

/**
 * 获取图片dom
 * @param realpath相对路径
 * @param base64图片信息
 */
export async function loadImageDom(
  base64: string
): Promise<HTMLImageElement | null> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = base64;
    image.onload = () => {
      resolve(image);
    };
    image.onerror = (error) => {
      console.warn('loadImageDom发生错误', error);
      reject(error);
    };
  });
}

/**
 * 图片的arrgybuffer数据转base64
 * @param arraybuffer
 * @param imageFilename 图片名称， 用于获取图片类型
 * @returns
 */
export const arraybufferToBase64 = (
  arraybuffer: ArrayBuffer,
  imageFilename: string
) => {
  const blob = new Blob([arraybuffer]);
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  return new Promise<string | undefined>((resolve, reject) => {
    reader.onload = () => {
      const type = mime.getType(imageFilename) ?? 'image/png';
      const base64 = reader.result
        ?.toString()
        .replace(/data:.*;base64,/, `data:${type};base64,`);
      resolve(base64);
    };
    reader.onerror = (e) => {
      console.log(e);

      reject();
    };
  });
};

/**
 * 返回图片base64数据
 * @param imagePath 图片目录
 */
export const getImageBase64 = async (imagePath: string) => {
  const fs = ElectronApi.fs;
  const path = ElectronApi.path;

  if (!imagePath || !fs || !path || !isImageFile(imagePath)) return;

  const bf = await fs?.promises.readFile(imagePath);
  if (!bf) return;
  return await arraybufferToBase64(
    bf.buffer as unknown as ArrayBuffer,
    imagePath
  );
};

/**
 * base64转blob
 * @param dataurl
 * @returns
 */
export function dataURItoBlob(dataURI: string) {
  const byteString = atob(dataURI.split(',')[1]);
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeString });
}

export const writeImage = async (imageName: string, base64: string) => {
  const blob = dataURItoBlob(base64);
  const arrBf = await blob.arrayBuffer();
  const dataView = new DataView(arrBf);
  await ElectronApi.fs?.promises.writeFile(imageName, dataView);
};

/**
 * 返回图片尺寸信息，参数二选一
 * @param imagePath [可选] 图片在磁盘位置
 * @param base64 [可选] 图片base64数据
 * @returns
 */
export const getImageSize = async (imagePath?: string, base64?: string) => {
  if (!base64) {
    if (!imagePath) {
      throw 'imagePath为空，当base64不存在时，imagePath必须提供';
    }
    base64 = base64 ?? (await getImageBase64(imagePath));
  }
  if (!base64) return;

  const imgDom = await loadImageDom(base64).catch(() => {
    return null;
  });
  return {
    width: imgDom?.width,
    height: imgDom?.height
  };
};

/**
 * 返回文件大小
 * @param filePath 图片目录
 */
export const getFileSize = async (filePath: string) => {
  const fs = ElectronApi.fs;
  const path = ElectronApi.path;

  if (!filePath || !fs || !path || !isImageFile(filePath)) return;

  const bf = await fs?.promises.readFile(filePath);
  if (!bf) return;
  const size = await new Promise((res) => {
    fs.stat(filePath, (err, stats) => {
      if (err) throw err;
      else res(stats.size);
    });
  });
  return size;
};

/**
 * 图片是否有透明像素
 * @param filePath 图片目录
 */
export const imageHasAlpha = async (filePath: string) => {
  const img = await getImageBase64(filePath);
  const imgDom = (await loadImageDom(img as string)) as HTMLImageElement;
  const canvas = document.createElement('canvas');
  [canvas.width, canvas.height] = [imgDom.width, imgDom.height];
  const ctx = canvas.getContext('2d', {
    willReadFrequently: true
  }) as CanvasRenderingContext2D;
  ctx.drawImage(imgDom, 0, 0);
  const data = ctx.getImageData(0, 0, imgDom.width, imgDom.height);
  const alpha = data.data;
  for (let i = 0; i < alpha.length; i += 4)
    if (alpha[i + 3] !== 255) return true;
  return false;
};
